import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
  Fade,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api';
import axios from 'axios';
import { requestLoginOTP, verifyLoginOTP } from '../services/otpService';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();

  // Countdown timer for OTP
  useEffect(() => {
    if (!showOTP) {
      setTimeLeft(300);
      setCanResend(false);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Enable resend after 60 seconds
    const resendTimer = setTimeout(() => {
      setCanResend(true);
    }, 60000);

    return () => {
      clearInterval(timer);
      clearTimeout(resendTimer);
    };
  }, [showOTP]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'email' ? value.toLowerCase() : value,
    });
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('');
    while (newOtp.length < 4) newOtp.push('');

    setOtp(newOtp);
    inputRefs[3].current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Request OTP
      const response = await requestLoginOTP(formData.email, formData.password);

      if (response.status) {
        setShowOTP(true);
        showToast('OTP sent to your email and device', 'success');
        // Focus first OTP input after transition
        setTimeout(() => inputRefs[0].current?.focus(), 300);
      }
    } catch (error) {
      console.error('Login error:', error);
      showToast(
        error.message || 'Login failed. Please check your credentials.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== 4) {
      setOtpError('Please enter the complete 4-digit OTP');
      return;
    }

    setLoading(true);
    setOtpError('');

    try {
      const response = await verifyLoginOTP(formData.email, otpValue);

      if (response.status) {
        // Store token in localStorage
        localStorage.setItem('token', response.token);
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(response.user));

        // Update auth context
        await login(response);

        showToast('Login successful', 'success');

        // Use window.location for full page reload to ensure proper state initialization
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPResend = async () => {
    setLoading(true);
    setOtpError('');
    setOtp(['', '', '', '']);

    try {
      const response = await requestLoginOTP(formData.email, formData.password);

      if (response.status) {
        setTimeLeft(300);
        setCanResend(false);
        showToast('New OTP sent successfully', 'success');
        inputRefs[0].current?.focus();
      }
    } catch (error) {
      console.error('OTP resend error:', error);
      setOtpError(error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOTP(false);
    setOtp(['', '', '', '']);
    setOtpError('');
    setTimeLeft(300);
    setCanResend(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundImage: 'url(/loginbg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 100%)'
            : 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 100%)',
          backdropFilter: 'blur(2px)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ height: '100vh', py: 4, position: 'relative', zIndex: 1 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Left side - Background Image */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundImage: 'url(/loginbg.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                '&::before': {
                  background: `linear-gradient(45deg, ${alpha('#a90b0a', 0.85)} 0%, ${alpha('#8b0000', 0.9)} 100%)`,
                },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(45deg, ${alpha('#a90b0a', 0.9)} 0%, ${alpha('#8b0000', 0.95)} 100%)`,
                zIndex: 1,
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box
                component="img"
                src="/images/logo.png"
                alt="Utilita Logo"
                sx={{
                  width: 140,
                  height: 'auto',
                  mb: 4,
                  filter: 'brightness(0) invert(1)',
                  animation: 'logoFloat 3s ease-in-out infinite',
                  dropShadow: '0 4px 8px rgba(0,0,0,0.3)',
                }}
              />

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  animation: 'slideUp 0.8s ease-out',
                  background: `linear-gradient(45deg, #ffffff 0%, ${alpha('#ffffff', 0.9)} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                }}
              >
                Welcome to Utilita
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  opacity: 0.95,
                  maxWidth: 450,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  animation: 'slideUp 1s ease-out',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Your all-in-one utility management platform
              </Typography>
            </Box>
          </Grid>

          {/* Right side - Login/OTP Form */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, md: 4 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                width: '100%',
                maxWidth: 450,
                borderRadius: 3,
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.95)
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                  : '0 8px 32px rgba(0, 0, 0, 0.1)',
                animation: 'fadeIn 0.5s ease-out',
                '&:hover': {
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 12px 48px rgba(0, 0, 0, 0.4)'
                    : '0 12px 48px rgba(0, 0, 0, 0.15)',
                },
                transition: 'all 0.3s ease-in-out',
                border: theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
                  : 'none',
              }}
            >
              {!showOTP ? (
                // Email/Password Form
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        animation: 'scaleIn 0.5s ease-out',
                      }}
                    >
                      <LockOutlinedIcon
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: 32,
                          animation: 'bounce 1s ease-in-out',
                        }}
                      />
                    </Box>
                    <Typography
                      component="h1"
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mb: 1,
                        animation: 'slideUp 0.5s ease-out',
                      }}
                    >
                      Welcome Back
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        animation: 'slideUp 0.7s ease-out',
                      }}
                    >
                      Sign in to continue to your dashboard
                    </Typography>
                  </Box>

                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      margin="normal"
                      required
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: 'text.secondary', opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.9) : 'background.paper',
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2,
                            },
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.95) : 'background.paper',
                          },
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      margin="normal"
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.9) : 'background.paper',
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2,
                            },
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.95) : 'background.paper',
                          },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                    </Button>
                  </form>
                </>
              ) : (
                // OTP Input Form
                <Fade in={showOTP}>
                  <Box>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <LockOutlinedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold" color="text.primary">
                        Two-Factor Authentication
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Verification required for: <strong>Admin Login</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enter the 4-digit code sent to your email and device
                      </Typography>
                    </Box>

                    {otpError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {otpError}
                      </Alert>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        mb: 3,
                      }}
                      onPaste={handleOTPPaste}
                    >
                      {otp.map((digit, index) => (
                        <TextField
                          key={index}
                          inputRef={inputRefs[index]}
                          value={digit}
                          onChange={(e) => handleOTPChange(index, e.target.value)}
                          onKeyDown={(e) => handleOTPKeyDown(index, e)}
                          inputProps={{
                            maxLength: 1,
                            style: {
                              textAlign: 'center',
                              fontSize: '32px',
                              fontWeight: 'bold',
                            },
                          }}
                          sx={{
                            width: 60,
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.dark,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography
                        variant="h6"
                        color={timeLeft < 60 ? 'error.main' : 'text.secondary'}
                        fontWeight="bold"
                      >
                        {formatTime(timeLeft)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Time remaining
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Didn't receive the code?{' '}
                        <Button
                          onClick={handleOTPResend}
                          disabled={!canResend || loading}
                          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                        >
                          Resend OTP
                        </Button>
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={handleBackToLogin}
                        variant="outlined"
                        fullWidth
                        disabled={loading}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleOTPVerify}
                        variant="contained"
                        fullWidth
                        disabled={loading || otp.join('').length !== 4}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          @keyframes logoFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-15px); }
          }
        `}
      </style>
    </Box>
  );
};

export default Login;